/**
 * Shared upgrade-navigation contract.
 *
 * Every surface that routes a user to the /upgrade page goes through
 * `goToUpgrade()`, which emits the `upgrade:clicked` analytics event and then
 * navigates via the SPA `navigate` event. AnalyticsService forwards the event
 * to GA4 as `upgrade_clicked` with `{component, location}` params.
 *
 * The `{component, location}` values come from the bounded vocabulary below —
 * callers pass a surface key, never free-form strings, so GA4 dimension values
 * stay a closed set. Adding a surface means adding a row here (and registering
 * nothing new in GA4 — `component`/`location` are already registered
 * dimensions; see project-docs/components/30-analytics.md).
 */

/**
 * Bounded `{component, location}` vocabulary, keyed by surface.
 * @type {Object.<string, {component: string, location: string}>}
 */
export const UPGRADE_CLICK_SURFACES = Object.freeze({
  /** Header "Upgrade" button (UpgradeService's #header-upgrade-btn) */
  headerButton: Object.freeze({ component: 'headerButton', location: 'header' }),
  /** Sidebar profile-area upgrade button (app.js) */
  sidebarButton: Object.freeze({ component: 'sidebarButton', location: 'sidebar' }),
  /** Global usage banner's "Upgrade for unlimited" link (UsageService) */
  usageBanner: Object.freeze({ component: 'usageBanner', location: 'banner' }),
  /** Lock overlay's inline text link — dashboard variant (Input.js) */
  lockOverlayLink: Object.freeze({ component: 'lockOverlay', location: 'dashboard' }),
  /** Floating "Upgrade for unlimited" pill above the chat input (app.js) */
  upgradePill: Object.freeze({ component: 'upgradePill', location: 'chat' }),
  /** PartnerSection's partner-rate upsell "See Premium" (PartnerSection) */
  partnerUpsell: Object.freeze({ component: 'partnerUpsell', location: 'partner' }),
  /** ProfilePopup's upgrade row for Basic users (ProfilePopup) */
  profilePopup: Object.freeze({ component: 'profilePopup', location: 'profileMenu' }),
  /** Guide preview gate's end-of-preview CTA (guideDrillHelper). The merged
   *  reader gates both halves of a hybrid guide behind ONE gate (D6), so this
   *  is the only guide-preview surface: the retired `sharedGuidePreview` row
   *  costed the funnel one dimension, knowingly. */
  guidePreview: Object.freeze({ component: 'guidePreview', location: 'guide' }),
  /** A PREMIUM_REQUIRED 403 refusing path entry (ModeOrchestrator) */
  pathEntry: Object.freeze({ component: 'pathEntry', location: 'discovery' }),
  /** A `premium_required` path's detail panel — its header primary button
   *  ("Upgrade to Premium"), in the slot a workable path's "Begin this path"
   *  takes (DiscoverySection.showDetailView) */
  pathDetail: Object.freeze({ component: 'pathDetail', location: 'discovery' }),
  /** The one "See Premium" pill trailing the Paths sheet's "Available with
   *  Premium" group label — one offer per run of locked rows, never per row
   *  (DiscoverySection.renderPremiumPathsNotice) */
  pathGroup: Object.freeze({ component: 'pathGroup', location: 'discovery' }),
  /** Account settings subscription card's "Upgrade" (AccountSettingsSection) */
  accountSettings: Object.freeze({ component: 'accountSettings', location: 'settings' })
});

/**
 * Emit `upgrade:clicked` for a known surface, then navigate to /upgrade.
 *
 * The click event is emitted before the `navigate` event so the analytics
 * dispatch cannot be lost to a page teardown. An unknown surface key still
 * navigates (never break the user's click) but reports `unknown` rather than
 * inventing a vocabulary value.
 *
 * @param {Object} eventBus - App event bus
 * @param {string} surface - Key into UPGRADE_CLICK_SURFACES
 */
export function goToUpgrade(eventBus, surface) {
  const meta = UPGRADE_CLICK_SURFACES[surface]
    || { component: 'unknown', location: 'unknown' };
  eventBus?.emit?.('upgrade:clicked', { component: meta.component, location: meta.location });
  eventBus?.emit?.('navigate', { page: 'upgrade' });
}
